<template>
  <div class="score-screen-page">
    <section class="hero-card">
      <div class="hero-copy">
        <div class="hero-title-row">
          <h1>顶伯计分屏助手</h1>
          <div class="hero-status-inline">
            <span class="hero-status-inline__label">本地助手</span>
            <span class="status-pill" :class="helperOnline ? 'online' : 'offline'">
              {{ helperOnline ? '已启动' : '未启动' }}
            </span>
            <span class="hero-status-inline__label">设备状态</span>
            <span class="status-pill" :class="devices.length > 0 ? 'online' : 'offline'">
              {{ devices.length > 0 ? `已识别 ${devices.length} 台` : '未检测到设备' }}
            </span>
          </div>
        </div>
        <div class="hero-tags">
          <span>Windows 安装包</span>
          <span>系统托盘常驻</span>
          <span>浏览器一键唤起</span>
        </div>
        <p class="device-status-note">
          {{ selectedDevice ? `当前设备：${selectedDevice.typeLabel} · ${selectedDevice.path}` : '当前设备：未选择设备' }}
        </p>
      </div>
      <div class="hero-actions">
        <el-button :loading="checkingHelper" @click="checkHelper(true)">检测本地助手</el-button>
        <el-dropdown v-if="!helperOnline" trigger="click" @command="handleHelperDownload">
          <el-button type="success">
            下载本地助手<i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="portable">下载免安装版</el-dropdown-item>
            <el-dropdown-item command="setup">下载安装包</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <el-button type="primary" :loading="launchingHelper" @click="openHelper">
          {{ helperOnline ? '重启本地助手' : '打开本地助手' }}
        </el-button>
        <el-button :disabled="!helperOnline" @click="deviceDrawerVisible = true">设备管理</el-button>
      </div>
    </section>

    <section class="workspace-section">
      <div class="panel-card team-panel">
        <div class="panel-head">
          <h2>代表队卡片</h2>
          <div class="team-count">
            <span>共 {{ teamCount }} 队</span>
            <label class="team-count__checkall">
              <input
                type="checkbox"
                :checked="allTeamsSelected"
                @change="toggleSelectAll"
              >
              <span>全选</span>
            </label>
            <el-button size="mini" @click="editTeamsDialogVisible = true">编辑代表队</el-button>
          </div>
        </div>
        <div class="team-card-grid">
          <button
            v-for="(team, index) in teams"
            :key="index"
            type="button"
            class="team-card"
            :class="{ active: isTeamSelected(index) }"
            @click="selectSingleTeam(index)"
          >
            <span class="team-card__check">
              <input
                type="checkbox"
                :checked="isTeamSelected(index)"
                @change="toggleTeamSelection(index)"
                @click.stop
              >
            </span>
            <div class="team-card__score-wrap">
              <strong class="team-card__score">{{ team.score }}</strong>
            </div>
            <span class="team-card__name">{{ team.name }}</span>
          </button>
        </div>
      </div>
    </section>

    <el-drawer
      title="设备管理"
      :visible.sync="deviceDrawerVisible"
      direction="rtl"
      size="360px"
      custom-class="device-drawer"
    >
      <div class="device-drawer__body">
        <div class="device-drawer__toolbar">
          <span class="panel-meta">已识别 {{ devices.length }} 台</span>
          <el-button
            size="mini"
            type="primary"
            :disabled="!helperOnline || scanLoading"
            :loading="scanLoading"
            @click="scanDevices"
          >
            扫描设备
          </el-button>
        </div>
        <div v-if="devices.length === 0" class="empty-block">
          <p>还没有识别到设备。</p>
          <p>请先确认本地助手已经启动，再点击“扫描设备”。</p>
        </div>
        <div v-else class="device-list">
          <button
            v-for="device in devices"
            :key="device.id"
            type="button"
            class="device-item"
            :class="{ active: selectedDeviceId === device.id }"
            @click="selectedDeviceId = device.id"
          >
            <div class="device-item__head">
              <strong>{{ device.typeLabel }}</strong>
              <span>{{ device.path }}</span>
            </div>
          </button>
        </div>
      </div>
    </el-drawer>

    <el-dialog
      title="编辑代表队"
      :visible.sync="editTeamsDialogVisible"
      width="760px"
      class="team-editor-dialog"
    >
      <div class="team-editor-toolbar">
        <span>代表队数量</span>
        <el-input-number v-model="teamCount" :min="1" :max="64" @change="resetTeams" />
      </div>
      <div class="team-editor-grid">
        <div
          v-for="(team, index) in teams"
          :key="index"
          class="team-editor-row"
          :class="{ active: isTeamSelected(index) }"
          @click="selectSingleTeam(index)"
        >
          <span class="team-editor-row__index">{{ index + 1 }}</span>
          <el-input v-model="team.name" size="mini" @click.native.stop @input="persistTeamSettings" />
          <el-button type="text" class="team-editor-row__use" @click.stop="toggleTeamSelection(index)">
            {{ isTeamSelected(index) ? '取消' : '选择' }}
          </el-button>
        </div>
      </div>
    </el-dialog>

    <section class="panel-card score-action-card">
      <div class="score-quick-actions">
        <el-button size="mini" :disabled="!hasTeamSelection || scoreActionBusy" @click="applyScoreAction('add', 1)">
          +1
        </el-button>
        <el-button size="mini" :disabled="!hasTeamSelection || scoreActionBusy" @click="applyScoreAction('add', 5)">
          +5
        </el-button>
        <el-button size="mini" type="primary" :disabled="!hasTeamSelection || scoreActionBusy" @click="applyScoreAction('add', 10)">
          +10
        </el-button>
        <el-button size="mini" :disabled="!hasTeamSelection || scoreActionBusy" @click="applyScoreAction('subtract', 1)">
          -1
        </el-button>
        <el-button size="mini" :disabled="!hasTeamSelection || scoreActionBusy" @click="applyScoreAction('subtract', 5)">
          -5
        </el-button>
        <el-button size="mini" :disabled="!hasTeamSelection || scoreActionBusy" @click="applyScoreAction('subtract', 10)">
          -10
        </el-button>
      </div>
      <div class="score-form">
        <el-input-number v-model="bulkScoreValue" :min="0" :max="65535" :disabled="scoreActionBusy" />
        <el-button type="primary" :disabled="!hasTeamSelection || scoreActionBusy" @click="applyScoreAction('add')">
          加分
        </el-button>
        <el-button :disabled="!hasTeamSelection || scoreActionBusy" @click="applyScoreAction('subtract')">
          减分
        </el-button>
        <el-button type="success" :disabled="!hasTeamSelection || scoreActionBusy" @click="applyScoreAction('set')">
          设置初始值
        </el-button>
        <el-button type="danger" plain :disabled="!hasTeamSelection || scoreActionBusy" @click="applyScoreAction('reset')">
          分数清零
        </el-button>
      </div>
      <p class="score-feedback" :class="[scoreActionMessageType, { 'is-hidden': !scoreActionMessage }]">
        {{ scoreActionMessage }}
      </p>
    </section>

    <section v-if="selectedDevice && selectedDevice.type === 'db-s4'" class="panel-card">
      <div class="panel-head">
        <h2>一体机控制</h2>
      </div>
      <div class="command-grid">
        <el-button type="warning" :disabled="!hasTeamSelection || commandBusy" @click="openColorDialog">设置颜色</el-button>
        <el-button :disabled="!hasTeamSelection || commandBusy" @click="sendCommand('lightMode1')">增加亮度</el-button>
        <el-button :disabled="!hasTeamSelection || commandBusy" @click="sendCommand('lightMode2')">减小亮度</el-button>
        <el-button :disabled="!hasTeamSelection || commandBusy" @click="sendCommand('open')">打开电量板</el-button>
        <el-button :disabled="!hasTeamSelection || commandBusy" @click="sendCommand('close')">关闭电量板</el-button>
        <el-button :disabled="!hasTeamSelection || commandBusy" @click="sendCommand('test')">测 试</el-button>
        <el-button :disabled="!hasTeamSelection || commandBusy" @click="sendCommand('showInfo')">显示编码</el-button>
        <el-button :disabled="!hasTeamSelection || commandBusy" @click="sendCommand('duima')">对 码</el-button>
      </div>
    </section>

    <el-dialog
      title="计分屏颜色设置"
      :visible.sync="colorDialogVisible"
      width="460px"
      class="color-config-dialog"
    >
      <div class="color-preview-panel">
        <div class="color-preview-screen">
          <div
            v-for="(color, index) in colorPreviewColors"
            :key="index"
            class="led-digit"
            :style="{ '--led-color': color }"
          >
            <span class="led-segment led-segment--a is-on"></span>
            <span class="led-segment led-segment--b is-on"></span>
            <span class="led-segment led-segment--c is-on"></span>
            <span class="led-segment led-segment--d is-on"></span>
            <span class="led-segment led-segment--e is-on"></span>
            <span class="led-segment led-segment--f is-on"></span>
            <span class="led-segment led-segment--g is-on"></span>
          </div>
        </div>
        <p>颜色预览：三位数字都会显示为 8，对应上方三组颜色设置。</p>
      </div>
      <div class="color-config-groups">
        <div class="color-config-group">
          <h3>第一位数字</h3>
          <label v-for="item in colorGroup3" :key="item.key" class="color-check">
            <input v-model="colorSelections[item.key]" type="checkbox">
            <span>{{ item.label }}</span>
          </label>
        </div>
        <div class="color-config-group">
          <h3>第二位数字</h3>
          <label v-for="item in colorGroup2" :key="item.key" class="color-check">
            <input v-model="colorSelections[item.key]" type="checkbox">
            <span>{{ item.label }}</span>
          </label>
        </div>
        <div class="color-config-group">
          <h3>第三位数字</h3>
          <label v-for="item in colorGroup1" :key="item.key" class="color-check">
            <input v-model="colorSelections[item.key]" type="checkbox">
            <span>{{ item.label }}</span>
          </label>
        </div>
      </div>
      <p v-if="colorDialogError" class="color-config-error">{{ colorDialogError }}</p>
      <span slot="footer" class="dialog-footer">
        <el-button @click="closeColorDialog">关闭</el-button>
        <el-button type="primary" :disabled="commandBusy" @click="confirmColorDialog">确认</el-button>
      </span>
    </el-dialog>

  </div>
</template>

<script>
import axios from 'axios'

const helperClient = axios.create({
  baseURL: 'http://127.0.0.1:18540',
  timeout: 2500
})

const HELPER_PROTOCOL_URL = 'dbtool-score-helper://open'
const HELPER_SETUP_PATH = '/downloads/dbtool-score-helper-setup.exe'
const HELPER_PORTABLE_PATH = '/downloads/dbtool-score-helper-portable.exe'
const TEAM_SETTINGS_STORAGE_KEY = 'score-screen-helper-team-settings'

function createTeams(count) {
  return Array.from({ length: count }, (_, index) => ({
    name: `代表队 ${index + 1}`,
    score: 10
  }))
}

function resolvePublicAssetUrl(path) {
  const assetPath = String(path || '').replace(/^\/+/, '')
  const pageUrl = window.location.href.split('#')[0]
  return new URL(assetPath, pageUrl).toString()
}

export default {
  name: 'ScoreScreenBridge',
  created() {
    this.hydrateTeamSettings()
  },
  data() {
    return {
      helperBaseUrl: 'http://127.0.0.1:18540',
      helperOnline: false,
      helperVersion: '',
      helperCapabilities: {},
      helperError: '',
      lastScanAt: '',
      checkingHelper: false,
      scanLoading: false,
      launchingHelper: false,
      helperPollTimer: null,
      devices: [],
      selectedDeviceId: '',
      teamCount: 8,
      teams: createTeams(8),
      editTeamsDialogVisible: false,
      deviceDrawerVisible: false,
      selectedTeamIndexes: [],
      bulkScoreValue: 10,
      scoreActionLoading: '',
      commandLoading: '',
      lastResponse: '',
      scoreActionMessage: '',
      scoreActionMessageType: '',
      colorDialogVisible: false,
      colorDialogError: '',
      colorSelections: {
        red1: false,
        green1: false,
        blue1: false,
        red2: false,
        green2: false,
        blue2: false,
        red3: false,
        green3: false,
        blue3: false
      }
    }
  },
  computed: {
    helperSetupUrl() {
      return resolvePublicAssetUrl(HELPER_SETUP_PATH)
    },
    helperPortableUrl() {
      return resolvePublicAssetUrl(HELPER_PORTABLE_PATH)
    },
    selectedDevice() {
      return this.devices.find(item => item.id === this.selectedDeviceId) || null
    },
    selectedTeamNumbers() {
      return this.selectedTeamIndexes
        .slice()
        .sort((a, b) => a - b)
        .map(index => index + 1)
    },
    allTeamsSelected() {
      return this.teams.length > 0 && this.selectedTeamIndexes.length === this.teams.length
    },
    hasTeamSelection() {
      return this.selectedTeamIndexes.length > 0
    },
    primarySelectedTeamNumber() {
      return this.selectedTeamNumbers[0] || 1
    },
    scoreActionBusy() {
      return !!this.scoreActionLoading
    },
    commandBusy() {
      return !!this.commandLoading
    },
    colorGroup1() {
      return [
        { key: 'red1', label: '红色' },
        { key: 'green1', label: '绿色' },
        { key: 'blue1', label: '蓝色' }
      ]
    },
    colorGroup2() {
      return [
        { key: 'red2', label: '红色' },
        { key: 'green2', label: '绿色' },
        { key: 'blue2', label: '蓝色' }
      ]
    },
    colorGroup3() {
      return [
        { key: 'red3', label: '红色' },
        { key: 'green3', label: '绿色' },
        { key: 'blue3', label: '蓝色' }
      ]
    },
    colorPreviewColors() {
      return [
        this.getPreviewColorForDigit(3),
        this.getPreviewColorForDigit(2),
        this.getPreviewColorForDigit(1)
      ]
    }
  },
  mounted() {
    this.checkHelper()
  },
  beforeDestroy() {
    this.stopHelperPolling()
  },
  methods: {
    hydrateTeamSettings() {
      try {
        const raw = window.localStorage.getItem(TEAM_SETTINGS_STORAGE_KEY)
        if (!raw) return
        const parsed = JSON.parse(raw)
        const nextCount = Math.min(64, Math.max(1, Number(parsed && parsed.teamCount) || 8))
        const storedNames = Array.isArray(parsed && parsed.teamNames) ? parsed.teamNames : []
        const nextTeams = createTeams(nextCount).map((team, index) => ({
          ...team,
          name: storedNames[index] ? String(storedNames[index]) : team.name
        }))
        this.teamCount = nextCount
        this.teams = nextTeams
      } catch (error) {
        // ignore invalid local cache
      }
    },
    persistTeamSettings() {
      try {
        const payload = {
          teamCount: this.teamCount,
          teamNames: this.teams.slice(0, this.teamCount).map(team => String(team.name || ''))
        }
        window.localStorage.setItem(TEAM_SETTINGS_STORAGE_KEY, JSON.stringify(payload))
      } catch (error) {
        // ignore localStorage failures
      }
    },
    resetTeams() {
      const nextCount = Math.min(64, Math.max(1, Number(this.teamCount) || 1))
      const nextTeams = createTeams(nextCount).map((team, index) => {
        const existing = this.teams[index]
        return existing ? { ...team, name: existing.name, score: existing.score || 0 } : team
      })
      this.teamCount = nextCount
      this.teams = nextTeams
      this.selectedTeamIndexes = this.selectedTeamIndexes.filter(index => index < nextCount)
      this.persistTeamSettings()
    },
    isTeamSelected(index) {
      return this.selectedTeamIndexes.includes(index)
    },
    selectSingleTeam(index) {
      this.selectedTeamIndexes = [index]
    },
    toggleTeamSelection(index) {
      if (this.isTeamSelected(index)) {
        this.selectedTeamIndexes = this.selectedTeamIndexes.filter(item => item !== index)
        return
      }
      this.selectedTeamIndexes = [...this.selectedTeamIndexes, index].sort((a, b) => a - b)
    },
    toggleSelectAll() {
      if (this.allTeamsSelected) {
        this.selectedTeamIndexes = []
        return
      }
      this.selectedTeamIndexes = this.teams.map((_team, index) => index)
    },
    async probeHelper() {
      const res = await helperClient.get('/health')
      return res.data || {}
    },
    async refreshDevices() {
      const res = await helperClient.post('/scan')
      this.devices = Array.isArray(res.data && res.data.devices) ? res.data.devices : []
      this.lastScanAt = (res.data && res.data.lastScanAt) || ''
      if (!this.devices.find(item => item.id === this.selectedDeviceId)) {
        this.selectedDeviceId = this.devices[0] ? this.devices[0].id : ''
      }
    },
    async checkHelper(showToast) {
      this.checkingHelper = true
      this.helperError = ''
      try {
        const data = await this.probeHelper()
        this.helperOnline = !!data.ok
        this.helperVersion = data.version || ''
        this.helperCapabilities = (data && data.capabilities) || {}
        this.lastScanAt = data.lastScanAt || ''
        if (this.helperOnline) {
          await this.refreshDevices()
          if (showToast) this.setScoreActionMessage('已检测到本地助手', 'success')
        }
      } catch (error) {
        this.helperOnline = false
        this.helperVersion = ''
        this.helperCapabilities = {}
        this.lastScanAt = ''
        this.devices = []
        this.selectedDeviceId = ''
        if (showToast) {
          this.helperError = '本地助手未响应，请先下载安装并启动本地助手。'
        }
      } finally {
        this.checkingHelper = false
      }
    },
    handleHelperDownload(command) {
      this.downloadHelper(command)
    },
    downloadHelper(kind = 'setup') {
      const targetUrl = kind === 'portable' ? this.helperPortableUrl : this.helperSetupUrl
      window.open(targetUrl, '_blank')
    },
    openHelper() {
      this.helperError = ''
      this.launchingHelper = true
      this.triggerProtocolOpen()
      this.waitForHelperOnline()
    },
    triggerProtocolOpen() {
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = HELPER_PROTOCOL_URL
      document.body.appendChild(iframe)
      window.setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
      }, 1200)
    },
    waitForHelperOnline() {
      const deadline = Date.now() + 12000
      this.stopHelperPolling()

      const poll = async () => {
        try {
          const data = await this.probeHelper()
          this.helperOnline = !!data.ok
          this.helperVersion = data.version || ''
          this.helperCapabilities = (data && data.capabilities) || {}
          this.lastScanAt = data.lastScanAt || ''
          if (!this.helperOnline) return

          await this.refreshDevices()
          this.launchingHelper = false
          this.stopHelperPolling()
          this.setScoreActionMessage('本地助手已启动', 'success')
          return
        } catch (error) {
          if (Date.now() >= deadline) {
            this.launchingHelper = false
            this.stopHelperPolling()
            this.helperOnline = false
            this.helperVersion = ''
            this.helperCapabilities = {}
            this.lastScanAt = ''
            this.devices = []
            this.selectedDeviceId = ''
            this.helperError = '未检测到本地助手响应，若尚未安装，请先下载安装包。'
          }
        }
      }

      poll()
      this.helperPollTimer = window.setInterval(poll, 1000)
    },
    stopHelperPolling() {
      if (!this.helperPollTimer) return
      window.clearInterval(this.helperPollTimer)
      this.helperPollTimer = null
    },
    async scanDevices() {
      this.scanLoading = true
      this.helperError = ''
      try {
        const res = await helperClient.post('/scan')
        this.devices = Array.isArray(res.data && res.data.devices) ? res.data.devices : []
        this.lastScanAt = (res.data && res.data.lastScanAt) || ''
        if (!this.devices.find(item => item.id === this.selectedDeviceId)) {
          this.selectedDeviceId = this.devices[0] ? this.devices[0].id : ''
        }
        this.lastResponse = JSON.stringify(res.data, null, 2)
      } catch (error) {
        this.helperError = this.extractMessage(error, '扫描设备失败')
      } finally {
        this.scanLoading = false
      }
    },
    setScoreActionMessage(message, type = 'info') {
      this.scoreActionMessage = message
      this.scoreActionMessageType = type
    },
    isMissingDeviceError(message) {
      const text = String(message || '')
      return /Opening\s+COM\d+.*File not found/i.test(text) || /File not found/i.test(text)
    },
    getNextTeamScore(action, currentScore, inputScore) {
      if (action === 'add') return this.clampScore(currentScore + inputScore)
      if (action === 'subtract') return this.clampScore(currentScore - inputScore)
      if (action === 'set') return inputScore
      if (action === 'reset') return 0
      return currentScore
    },
    applyLocalScoreUpdates(action, inputScore) {
      for (const teamNumber of this.selectedTeamNumbers) {
        const index = teamNumber - 1
        const team = this.teams[index]
        if (!team) continue
        const currentScore = this.clampScore(team.score)
        const score = this.getNextTeamScore(action, currentScore, inputScore)
        this.$set(this.teams, index, {
          ...team,
          score
        })
      }
    },
    async handleDeviceMissing(message) {
      this.selectedDeviceId = ''
      try {
        await this.refreshDevices()
      } catch (error) {
        this.devices = []
        this.selectedDeviceId = ''
      }
      this.setScoreActionMessage(message || '设备已断开，已切换为仅更新页面分数', 'error')
    },
    clampScore(value) {
      return Math.min(65535, Math.max(0, Math.round(Number(value) || 0)))
    },
    getPreviewColorForDigit(digitIndex) {
      const red = !!this.colorSelections[`red${digitIndex}`]
      const green = !!this.colorSelections[`green${digitIndex}`]
      const blue = !!this.colorSelections[`blue${digitIndex}`]
      if (!red && !green && !blue) return 'rgba(148, 163, 184, 0.18)'
      const r = red ? 255 : 0
      const g = green ? 255 : 0
      const b = blue ? 255 : 0
      return `rgb(${r}, ${g}, ${b})`
    },
    getColorPayload() {
      let color1 = 0x00
      let color2 = 0x00
      if (this.colorSelections.red1) color2 = 0x80
      if (this.colorSelections.green1) color1 |= 0x04
      if (this.colorSelections.blue1) color1 |= 0x20
      if (this.colorSelections.red2) color1 |= 0x01
      if (this.colorSelections.green2) color1 |= 0x08
      if (this.colorSelections.blue2) color1 |= 0x40
      if (this.colorSelections.red3) color1 |= 0x02
      if (this.colorSelections.green3) color1 |= 0x10
      if (this.colorSelections.blue3) color1 |= 0x80
      return { color1, color2 }
    },
    openColorDialog() {
      this.colorDialogError = ''
      this.colorDialogVisible = true
    },
    closeColorDialog() {
      this.colorDialogVisible = false
      this.colorDialogError = ''
    },
    async confirmColorDialog() {
      const { color1, color2 } = this.getColorPayload()
      this.colorDialogError = ''
      if (color1 === 0xff || color1 === 0xfe) {
        this.colorDialogError = '不允许设置成白色，请重新设置'
        return
      }
      if (color1 === 0x00 && color2 === 0x00) {
        const confirmed = window.confirm('您确定要关闭屏幕吗？')
        if (!confirmed) return
      }
      const success = await this.sendCommand('setColor', { color1, color2 })
      if (success) this.closeColorDialog()
    },
    async applyScoreAction(action, overrideValue = null) {
      if (this.selectedTeamIndexes.length === 0) {
        this.setScoreActionMessage('请先选择代表队', 'error')
        return
      }
      this.setScoreActionMessage('', '')
      this.scoreActionLoading = action
      try {
        const inputScore = this.clampScore(overrideValue === null ? this.bulkScoreValue : overrideValue)
        let lastRes = null
        const shouldSendToDevice = !!this.selectedDevice
        const nextScores = []
        for (const teamNumber of this.selectedTeamNumbers) {
          const index = teamNumber - 1
          const team = this.teams[index]
          if (!team) continue
          const currentScore = this.clampScore(team.score)
          const score = this.getNextTeamScore(action, currentScore, inputScore)
          nextScores.push({ index, team, teamNumber, score })

          if (shouldSendToDevice) {
            try {
              const res = await helperClient.post('/send-score', {
                deviceId: this.selectedDevice.id,
                teamIndex: teamNumber,
                score
              })
              lastRes = res
            } catch (error) {
              const message = this.extractMessage(error, '分数操作失败')
              if (this.isMissingDeviceError(message)) {
                this.applyLocalScoreUpdates(action, inputScore)
                this.lastResponse = message
                await this.handleDeviceMissing(`设备已断开，已仅更新页面分数`)
                return
              }
              throw error
            }
          }
        }

        for (const item of nextScores) {
          this.$set(this.teams, item.index, {
            ...item.team,
            score: item.score
          })
        }
        if (lastRes) this.lastResponse = JSON.stringify(lastRes.data, null, 2)
        this.setScoreActionMessage(
          shouldSendToDevice
            ? `已同步到 ${this.selectedTeamIndexes.length} 个代表队`
            : `未检测到设备，已仅更新 ${this.selectedTeamIndexes.length} 个代表队的页面分数`,
          'success'
        )
      } catch (error) {
        const message = this.extractMessage(error, '分数操作失败')
        this.lastResponse = message
        this.setScoreActionMessage(message, 'error')
      } finally {
        this.scoreActionLoading = ''
      }
    },
    async sendCommand(action, extraPayload = {}) {
      if (!this.selectedDevice) {
        this.setScoreActionMessage('请先选择一个设备', 'error')
        return false
      }
      if (!this.hasTeamSelection) {
        this.setScoreActionMessage('请先选择代表队', 'error')
        return false
      }
      this.setScoreActionMessage('', '')
      this.commandLoading = action
      try {
        const payload = {
          deviceId: this.selectedDevice.id,
          action,
          selectedTeamNumbers: this.selectedTeamNumbers,
          allTeamsSelected: this.allTeamsSelected,
          teamCount: this.teams.length,
          teamIndex: this.primarySelectedTeamNumber,
          ...extraPayload
        }
        const res = await helperClient.post('/send-command', payload)
        this.lastResponse = JSON.stringify(res.data, null, 2)
        this.setScoreActionMessage('控制命令已发送', 'success')
        return true
      } catch (error) {
        const message = this.extractMessage(error, '发送控制命令失败')
        if (this.isMissingDeviceError(message)) {
          this.lastResponse = message
          await this.handleDeviceMissing('设备已断开，请重新扫描设备')
          return false
        }
        this.lastResponse = message
        this.setScoreActionMessage(message, 'error')
        return false
      } finally {
        this.commandLoading = ''
      }
    },
    extractMessage(error, fallback) {
      return (error.response && error.response.data && error.response.data.message) || error.message || fallback
    }
  }
}
</script>

<style lang="less" scoped>
.score-screen-page {
  display: grid;
  gap: 18px;
  color: var(--text-primary);
}

.hero-card,
.status-card,
.panel-card {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.97), rgba(240, 246, 255, 0.96));
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 22px;
  padding: 22px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
}

.hero-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 20px;
}

.hero-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 0 0 10px;
  flex-wrap: wrap;
}

.hero-copy h1 {
  margin: 0;
  font-size: 30px;
  line-height: 1.1;
}

.hero-copy p,
.status-text,
.form-tip,
.helper-error,
.offline-guide p,
.offline-guide li {
  color: var(--text-regular);
  line-height: 1.7;
}

.hero-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}

.device-status-note {
  margin: 14px 0 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--text-regular);
}

.hero-status-inline {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.hero-status-inline__label {
  font-size: 13px;
  color: var(--text-secondary);
}

.hero-tags span {
  padding: 7px 12px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  color: var(--text-secondary);
  font-size: 12px;
}

.hero-actions,
.install-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.status-head,
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.status-pill {
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-pill.online {
  background: rgba(34, 197, 94, 0.14);
  color: #15803d;
}

.status-pill.offline {
  background: rgba(239, 68, 68, 0.12);
  color: #b91c1c;
}

.offline-guide ol {
  margin: 10px 0 16px;
  padding-left: 20px;
}

.helper-error {
  margin: 12px 0 0;
  color: #b91c1c;
}

.workspace-section {
  display: block;
}

.panel-meta {
  color: var(--text-secondary);
  font-size: 13px;
}

.device-drawer__body {
  padding: 4px 16px 18px;
}

.device-drawer__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.device-list {
  display: grid;
  gap: 8px;
}

.device-item {
  width: 100%;
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.9);
  transition: border-color 0.16s ease, transform 0.16s ease, box-shadow 0.16s ease;
}

.device-item {
  padding: 10px 12px;
  text-align: left;
  cursor: pointer;
}

.device-item.active {
  border-color: rgba(14, 116, 144, 0.46);
  box-shadow: 0 12px 24px rgba(14, 116, 144, 0.12);
  transform: translateY(-1px);
}

.device-item__head,
.device-item__meta,
.score-form,
.command-grid,
.color-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.device-item__head {
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 0;
}

.team-count {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.score-action-card {
  padding: 14px 16px;
}

.score-quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.score-form,
.color-row {
  margin-bottom: 0;
}

.score-form {
  gap: 8px;
}

.score-form :deep(.el-input-number) {
  width: 160px;
}

.score-form :deep(.el-button) {
  min-width: 92px;
}

.score-feedback {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.4;
  min-height: 19px;
}

.score-feedback.success {
  color: #15803d;
}

.score-feedback.error {
  color: #b91c1c;
}

.score-feedback.is-hidden {
  visibility: hidden;
}

.command-grid {
  margin-bottom: 14px;
}

.color-config-groups {
  display: grid;
  gap: 12px;
}

.color-preview-panel {
  margin-bottom: 14px;
  padding: 16px 18px 14px;
  border-radius: 18px;
  background: linear-gradient(180deg, #08111f, #111827);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.color-preview-panel p {
  margin: 12px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: rgba(226, 232, 240, 0.72);
}

.color-preview-screen {
  display: flex;
  justify-content: center;
  gap: 18px;
}

.led-digit {
  --led-color: rgba(148, 163, 184, 0.18);
  position: relative;
  width: 54px;
  height: 96px;
  filter: drop-shadow(0 0 10px var(--led-color));
}

.led-segment {
  position: absolute;
  display: block;
  border-radius: 999px;
  background: rgba(71, 85, 105, 0.18);
  transition: background-color 0.18s ease, box-shadow 0.18s ease;
}

.led-segment.is-on {
  background: var(--led-color);
  box-shadow: 0 0 12px var(--led-color);
}

.led-segment--a,
.led-segment--d,
.led-segment--g {
  left: 11px;
  width: 32px;
  height: 8px;
}

.led-segment--a {
  top: 2px;
}

.led-segment--g {
  top: 44px;
}

.led-segment--d {
  bottom: 2px;
}

.led-segment--b,
.led-segment--c,
.led-segment--e,
.led-segment--f {
  width: 8px;
  height: 34px;
}

.led-segment--b,
.led-segment--c {
  right: 2px;
}

.led-segment--e,
.led-segment--f {
  left: 2px;
}

.led-segment--f,
.led-segment--b {
  top: 9px;
}

.led-segment--e,
.led-segment--c {
  bottom: 9px;
}

.color-config-group {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 16px;
  padding: 14px 16px;
  background: rgba(248, 250, 252, 0.85);
}

.color-config-group h3 {
  margin: 0 0 10px;
  font-size: 14px;
  color: #0f172a;
}

.color-check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: 20px;
  color: var(--text-regular);
  cursor: pointer;
}

.color-check input {
  cursor: pointer;
}

.color-config-error {
  margin: 12px 0 0;
  color: #b91c1c;
}

.result-card pre {
  margin: 0;
  padding: 16px;
  border-radius: 16px;
  background: #0f172a;
  color: #dbeafe;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.empty-block {
  border: 1px dashed rgba(15, 23, 42, 0.18);
  border-radius: 16px;
  padding: 18px;
  color: var(--text-secondary);
}

.team-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(136px, 1fr));
  gap: 10px;
}

.team-panel {
  width: 100%;
  box-sizing: border-box;
}

.score-action-card {
  width: 100%;
  box-sizing: border-box;
}

.team-card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 88px;
  padding: 10px 10px 8px;
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(241, 245, 249, 0.92));
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.06);
  cursor: pointer;
  text-align: left;
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
}

.team-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.1);
}

.team-card.active {
  border-color: rgba(14, 116, 144, 0.5);
  box-shadow: 0 16px 34px rgba(14, 116, 144, 0.16);
  background: linear-gradient(180deg, rgba(240, 253, 250, 0.98), rgba(236, 253, 245, 0.96));
}

.team-card__check {
  position: absolute;
  top: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
}

.team-card__check input {
  width: 15px;
  height: 15px;
  cursor: pointer;
}

.team-card__score-wrap {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  min-height: 42px;
}

.team-card__score {
  font-size: 24px;
  line-height: 1;
  color: #0f172a;
  letter-spacing: -0.04em;
  text-align: center;
}

.team-card.active .team-card__score {
  color: #0f766e;
}

.team-card__name {
  display: block;
  margin-top: auto;
  font-size: 12px;
  line-height: 1.35;
  color: var(--text-regular);
  word-break: break-word;
  text-align: center;
}

.team-selector-tip {
  margin-top: 12px;
  margin-bottom: 0;
}

.team-editor-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.team-editor-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.team-editor-row {
  display: grid;
  grid-template-columns: 48px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  transition: border-color 0.16s ease, transform 0.16s ease, box-shadow 0.16s ease;
}

.team-editor-row.active {
  border-color: rgba(14, 116, 144, 0.46);
  box-shadow: 0 12px 24px rgba(14, 116, 144, 0.12);
  transform: translateY(-1px);
}

.team-editor-row__index {
  font-size: 18px;
  font-weight: 700;
  color: #0f766e;
  text-align: center;
}

.team-editor-row__use {
  padding: 0 4px;
}

@media (max-width: 768px) {
  .hero-card {
    align-items: stretch;
  }

  .team-editor-toolbar {
    display: grid;
    grid-template-columns: 1fr;
  }

  .team-card-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .team-editor-grid {
    grid-template-columns: 1fr;
  }

  .team-editor-row {
    grid-template-columns: 40px 1fr;
  }

  .team-editor-row__use {
    justify-self: start;
  }
}
</style>
